
/**
 *     Data-driven UI that displays tables of data
 *
 *     @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *     Created on 06/02/2017
 *     Modified on 06/12/2018
 *
 */

/**
 * Check if requested page is valid
 * @param direction {string} - "previous" or "next"
 * @param currentView {object} - Backbone.Marionette current region view
 * @param model {object} - jQuery.Event model object
 * @param page {number} - page number to show
 * @returns {boolean}
 */
function isPageValid(direction, currentView, model, page) {
    var doUpdate = false,
        total;
    if (currentView && currentView.model && model && page) {
        total = currentView.model.get("total");
        if (direction === "next") {
            if (model.get("end") < total) {
                doUpdate = true;
            }
        }
        if (direction === "previous") {
            if (page > 1) {
                doUpdate = true;
            }
        }
    }

    return doUpdate;
}
/**
 * Update table body and table header with the new page of data
 * @param tableRegionName {string} - table region name
 * @param tableHeaderView {object} - Backbone.Marionette region view of the table header
 * @param page {number} - page number to render
 */
function updateTable( tableRegionName, tableHeaderView, page ) {
    var total,
        sortVars = {},
        sortField = null,
        sortOrder = null;

    if (tableRegionName && tableHeaderView && tableHeaderView.model) {
        total = tableHeaderView.model.get("total");

        sortVars = getRegionSortVars( tableRegionName );
        if ( hasKeys( sortVars ) ) {
            sortOrder = sortVars["sortOrder"] || null;
            sortField = sortVars["sortField"] || null;
        }

        updateTableHeader( tableHeaderView, page, total );
        updateTableBody( tableRegionName, page, sortField, sortOrder );

    }
}

/**
 * Fetch data from the stored response object and render new page of data in a table
 * @param tableRegionName {string} - table region name
 * @param page {number} - page number to render
 * @param sortField {string|null} - field to sort on
 * @param sortOrder {string|null} - sorting order
 */
function updateTableBody( tableRegionName, page, sortField, sortOrder ) {

    var view,
        tableRegionObject,
        fields,
        i,
        dataName,
        fieldNames,
        urls = [],
        recordJSON,
        apiJSON,
        recordCollection,
        recordModel,
        model,
        properties,
        propertiesCollection,
        dataNamesArray;

    // Get table region object from the global layout
    view = getRegionView( tableRegionName );

    if ( view ) {
        // Get table region view model
        tableRegionObject = getRegionObject( tableRegionName );
        if ( tableRegionObject ) {
            fields = tableRegionObject.fields;
            // Get the data needed to replace the view collection
            for ( i = 0; i < fields.length; i++ ) {
                dataName = fields[i].data;
                fieldNames = fields[i].names;

                // Get the data JSON model
                recordJSON = getJSONDataModel( dataName );
                if ( recordJSON ) {
                    recordCollection = getBackboneCollection( recordJSON.properties );
                    // Construct API URL for the data name
                    recordModel = new BackboneModel({
                        id: GlobalPageState.id,
                        pageName: GlobalPageState.currentPage,
                        page: page,
                        sortField: sortField,
                        sortOrder: sortOrder
                    });
                    dataNamesArray = [];
                    dataNamesArray.push( dataName );

                    apiJSON = getJSONModel( "api", GlobalPageState.type, null );

                    if ( apiJSON ) {
                        urls = constructAPIs( apiJSON, GlobalPageState.currentPage, dataNamesArray, recordModel );

                        if ( tableRegionObject.multiple && tableRegionObject.nested ) {
                            // Update table whose items are an array of nested objects

                            // convert recordCollection to collection of model properties
                            if ( recordCollection && recordCollection.models && recordCollection.models.length === 1 ) {
                                model = recordCollection.models[0];
                                if ( model ) {
                                    properties = model.get( "properties" );
                                    propertiesCollection = getBackboneCollection( properties );
                                    updateTableNestedArray( view, tableRegionObject, propertiesCollection, page );
                                }
                            }
                        }
                        else if ( urls.length > 0 ) {
                            // Fetch data from the server
                            getTableData( urls, dataNamesArray, page );
                        }
                    }
                }
            }
        }
    }
}

/**
 * Get data for the table
 * @param urls {[string]} -  array of URLs
 * @param dataNamesArray {[string]} - array of data names
 * @param page {number} - page number to get
 */
function getTableData(urls, dataNamesArray, page) {
    var response,
        collection,
        viewJSON,
        k;

    viewJSON = getJSONModel( "view", GlobalPageState.type, null );
    if ( viewJSON ) {
        collection = getBackboneCollection( viewJSON.properties );
        if ( collection ) {
            for ( k = 0; k < urls.length; k++ ) {
                response  = {};
                _.each( collection.models, function ( model ) {
                    if ( model.get("page") === GlobalPageState.currentPage ) {
                        makeAjaxRequest( urls[ k ], storeResponse( dataNamesArray[k], dataNamesArray, urls, response, GlobalPageState.currentPage, model, page ), dataNotFound( urls[ k ]) );
                    }
                });
            }
        }
    }
}

/**
 * Update a table of items that come from an array of nested objects
 * @param view {object} - event view
 * @param tableRegionObject {object} - JavaScript object representing table region
 * @param recordCollection {object} - Backbone.Collection containing record data
 * @param page {number} - page number to display
 */
function updateTableNestedArray(view, tableRegionObject, recordCollection, page) {
    var displayData = [],
        index = 0,
        fields,
        headerObject,
        headerFields,
        i,
        k,
        dataName,
        dataObject,
        fieldNames,
        fieldDataObject,
        displayDataArray = [],
        layoutCollection,
        fieldValues,
        collection,
        fieldData;

    if (view && tableRegionObject && recordCollection) {
        fields = tableRegionObject.fields;

        // Get the data needed to replace the view collection
        for (i = 0; i < fields.length; i++) {
            dataName = fields[i].data;
            fieldNames = fields[i].names;
            if (fieldNames) {
                fieldDataObject = getNestedFieldData(dataName, fieldNames);
                if (fieldDataObject) {
                    fieldData = fieldDataObject[dataName];
                }
            }

            if (fieldData) {
                collection = view.collection; // current collection of items to be replaced
                headerFields = tableRegionObject.additionalFields;
                headerObject = updateHeaderFields(headerFields, page, fieldData.length);

                for (k = headerObject.start - 1; k < headerObject.end; k++) {
                    displayData[index] = fieldData[k];
                    index++;
                }

                if (tableRegionObject.fields) {
                    for (i = 0; i < tableRegionObject.fields.length; i++) {
                        fields = tableRegionObject.fields[i];
                        if (fields) {
                            fieldValues = fields.names;
                        }
                    }
                    if (fieldValues) {
                        dataObject = parseArrayOfObjects( fieldValues, displayData, recordCollection, headerFields );
                        if ( dataObject ) {
                            displayDataArray.push( dataObject );

                            layoutCollection = getNestedMultipleLayoutCollection(tableRegionObject, displayDataArray);

                            if ( layoutCollection ) {
                                // Update collection models
                                collection.reset();
                                view.collection = layoutCollection;
                                view.render();
                            }
                        }
                    }
                }

            }
        }
    }
}

/**
 * Update table header model and global state
 * @param currentView {object} - Backbone.Marionette current region view
 * @param page {number} - current page number to update to
 * @param total {number} - total number of items in the table
 */
function updateTableHeader(currentView, page, total) {

    var model,
        regionId,
        parentId,
        parent,
        currentRegionName,
        headerModel,
        globalState,
        variables,
        variable,
        value,
        i,
        j;

    if (currentView) {

        // Update current view model
        model = currentView.model;
        regionId = currentView.cid;
        if (model && regionId) {
            headerModel = updateHeaderModel( model, page, total );
            currentView.model = headerModel;
            currentView.render();
        }

        // Update global page state for the region
        if (currentView.$el) {
            parent = currentView.$el.parent();
            if (parent) {
                parentId = parent.attr("id");
                if (parentId) {
                    currentRegionName = getRegionNameByDOMID(parentId);
                    if (currentRegionName) {
                        for (j = 0; j < GlobalPageState.length; j++) {
                            globalState = GlobalPageState[j];
                            if (globalState && globalState.name === currentRegionName) {
                                variables = globalState.variables;
                                for ( i = 0; i < variables.length; i++ ) {
                                    // Update global variables
                                    variable = variables[i];
                                    if ( variable && headerModel ){
                                        value = headerModel.get( variable.name );
                                        if ( value ) {
                                            variable.value = value;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Construct a table header object
 * @param model {object} - table header Backbone.Model
 * @param page {number} - page to show
 * @param total {number} - total number of items in the table
 * @returns {object}
 */
function updateHeaderModel(model, page, total) {
    var startIndex,
        endIndex,
        currentPage,
        newStartIndex,
        newEndIndex,
        itemsPerPage,
        newItemsPerPage,
        key;

    var attributes = model.attributes;
    var bbModel = new BackboneModel;

    for (key in attributes) {
        if (attributes.hasOwnProperty(key)) {
            if (key === "page") { currentPage = attributes[key]; }
            if (key === "start") { startIndex = attributes[key]; }
            if (key === "end") { endIndex = attributes[key]; }
            if (key === "itemsPerPage") { itemsPerPage = attributes[key]; }
        }
    }

    if (currentPage && currentPage !== page) {
        newItemsPerPage = endIndex - startIndex + 1;
        // next page
        if (currentPage + 1 === page && total > endIndex) { newStartIndex = endIndex + 1; } // last page
        newEndIndex = newItemsPerPage * page;
        if (newEndIndex > total) { newEndIndex = total; }  // last page

        // previous page
        if (currentPage - 1 === page) {
            if (startIndex > 1) { newEndIndex = startIndex - 1; }
            // first page
            if (page === 1) { newStartIndex = 1; }
            else {
                if (newEndIndex === total) {
                    // last page
                    newStartIndex = newEndIndex - newItemsPerPage * page;
                } else { newStartIndex = newEndIndex - itemsPerPage + 1; }
            }
        }

        // first page
        if (page === 1) {
            newStartIndex = 1;
            if (total < itemsPerPage) {
                newEndIndex = total;
            } else {
                newEndIndex = itemsPerPage;
            }
        }

        // check for boundary errors
        if (!newStartIndex || newStartIndex <= 0) { newStartIndex = 1; }
        if (!newEndIndex || (newEndIndex - newStartIndex > itemsPerPage)) { newEndIndex = newStartIndex + itemsPerPage; }

        for (key in attributes) {
            if (attributes.hasOwnProperty(key)) {
                if (key === "page") { model.set(key, page); }
                if (key === "start") { model.set(key, newStartIndex); }
                if (key === "end") { model.set(key, newEndIndex); }
                if (key === "itemsPerPage") { model.set(key, itemsPerPage); }
            }
        }
    }

    return model;
}
/**
 * Construct a table header object
 * @param additionalFields {object} - table header JavaScript object
 * @param page {number} - page to show
 * @param total {number} - total number of items in the table
 * @returns {object}
 */
function updateHeaderFields( additionalFields, page, total ) {
    var itemsPerPage,
        pagesObject,
        headerObject = {},
        i,
        k;

    for ( i = 0; i < additionalFields.length; i++ ) {
        if ( additionalFields[i].name === "itemsPerPage" ) {
            itemsPerPage = additionalFields[i].value;
        }
    }

    // Get the start and end indexes of the current table page
    pagesObject = getTablePages( total, itemsPerPage, page );

    if ( pagesObject && pagesObject.start !== undefined && pagesObject.end !== undefined ) {
            // Update additionalFields object with new values
        for ( i = 0; i < additionalFields.length; i++ ) {
            if ( additionalFields[i].name === "page" ) {
                additionalFields[i].value = page;
            }
            if ( additionalFields[i].name === "start" ) {
                additionalFields[i].value = pagesObject.start;
            }
            if ( additionalFields[i].name === "end" ) {
                additionalFields[i].value = pagesObject.end;
            }
        }

        for ( k = 0; k < additionalFields.length; k++ ) {
            headerObject[ additionalFields[ k ].name ] = additionalFields[ k ].value;
        }
    }

    return headerObject;
}

/**
 * Calculate start and end indexes of a pagination (index starts at 0)
 * @param total {number} - total number of items
 * @param itemsPerPage {number} - number of items per page
 * @param page {number} - current page
 * @returns {{start: *, end: *}}
 */
function getTablePages( total, itemsPerPage, page ) {
    var nPages,
        start,
        end;

    if ( total && itemsPerPage ) {
        nPages = Math.ceil( total / itemsPerPage );

        if ( page && nPages ) {
            start = ( page - 1 ) * itemsPerPage + 1;
            end = start + itemsPerPage - 1;

            if ( end > total ) {
                end = total;
            }
        }
    }

    return { start: start, end: end };
}
/**
 * Show table page triggered by a pagination event
 * @param event {object} - Backbone view that triggered the event
 * @param direction {string} - "previous" or "next"
 */
function showTablePage( event, direction ) {

    var model,
        view,
        page,
        regionId,
        tableHeaderView,
        stateVars,
        variable,
        tableRegionName,
        doUpdate,
        i;

    if (event) {
        model = event.model;
        view = event.view;

        if ( model && view ) {
            regionId = view.cid;
            tableHeaderView = getRegionViewById( regionId );
            stateVars = getRegionVars( regionId );

            if ( stateVars ) {
                for (i = 0; i < stateVars.length; i++) {
                    // Find "page" variable
                    variable = stateVars[i];
                    if (variable.name === "page") {

                        page = variable.value;
                        doUpdate = isPageValid( direction, tableHeaderView, model, page );

                        // Show the next page in the table
                        if (doUpdate) {
                            // Increment current page value
                            if ( direction === "next" ) { page++; }
                            if ( direction === "previous" ) { page--; }
                            tableRegionName = model.get( "region_name" );

                            if ( tableHeaderView ) {
                                updateTable( tableRegionName, tableHeaderView, page );
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Sort table of data
 * @param tableRegionName {string} - table region name
 * @param field {string} - field name to sort on
 * @param order {string} - sorting order
 */
function sortTable( tableRegionName, field, order ) {
    var page = 1, // when table is sorted in a new order, set the page back to 1
        tableView,
        tableHeaderView,
        sortData,
        success;

    if ( tableRegionName && field && order ) {
        tableView = getRegionView( tableRegionName );

        if ( tableView ) {

            // Find table header view
            tableHeaderView = getTableHeaderRegion( "region_name", tableRegionName );

            if ( tableHeaderView ) {
                sortData = { "sortOrder": order, "sortField": field };
                success = updateGlobalPageState( tableRegionName, sortData );
                if ( success ) {
                    updateTable( tableRegionName, tableHeaderView, page );
                }
            }
        }
    }
}

/**
 * Show next page in the table of items
 * @param event {jQuery.Event} - view that triggered the event
 */
function nextPage(event) {
    showTablePage(event, "next");
}

/**
 * Show previous page in the table of items
 * @param event {jQuery.Event} - view that triggered the event
 */
function previousPage(event) {
    showTablePage(event, "previous");
}

/**
 * Handle a click on a table row that redirects user to another page
 * @param event {object} - click event
 */
function clickRow(event) {
    var cells,
        cell,
        cellName,
        compoundName,
        i,
        id,
        value,
        type,
        newURL,
        oldURL,
        table,
        name,
        documentType;

    if (event && event.currentTarget) {
        // Get id value from the event row
        cells = event.currentTarget.cells;
        // Default ID field name
        name = "sarahEdiId";
        if ( cells ) {

            // Find the name of the cell with ID field
            for ( i = 0; i < cells.length; i++ ) {
                cell = cells[ i ];
                if ( cell ) {
                    cellName = $( cell ).attr( "name" );
                    if ( cellName && cellName.indexOf( "." ) > -1 ) {
                        compoundName = cellName.split( "." );
                        if ( compoundName && compoundName.length === 2 ) {
                            if ( compoundName[ 1 ] === name ) {
                                name = cellName;
                            }
                        }
                    }

                    // Get the type of the document from the Linked Document table row
                    if ( cellName === "documentType" ) {
                        documentType = $( cell ).attr( "value" );
                    }
                }
            }

            id = cells[ name ] || cells[ "_id" ] || cells[ "id" ];

            if ( id ) {
                value = $( id ).attr( "value" );
                if ( value && GlobalPageState ) {
                    // Check the type of data
                    table = $( id ).parents( 'table' );
                    if ( table ) {
                        // The table type is the type of the table that is displayed above the table, like "parents", "siblings", etc.
                        type = table.attr( "type" );
                        if ( type && GlobalPageState.type ) {
                            if ( type === "document" ) {
                              // Convert the generic document type into a specific document type from the documentType cell
                                type = documentType;
                                if ( type ) {
                                    type = type.toLowerCase();
                                }
                            }

                            // Redirect to the new single record page
                            if ( GlobalPageState.id && GlobalPageState.currentPage === "record" && type && value ) {
                                newURL = type + "/" + value;
                                oldURL = GlobalPageState.type + "/" + GlobalPageState.id;
                                updateEndURL( newURL, oldURL, true );
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Handle a click on a table header that sorts the table
 * @param event {object} - click event
 */
function clickSort( event ) {
    var order = 'd', // descending is the default sorting order
        tableRegionId,
        tableRegionName,
        cell,
        sortIcon,
        orderClass,
        table,
        parentEl,
        grandparentEl,
        field;

    if ( event && event.currentTarget ) {
        cell = $( event.currentTarget );
        if ( cell ) {
            sortIcon = cell.find('i.fa');
            if ( sortIcon ) {
                orderClass = $(sortIcon).attr("class");
                if ( orderClass ) {
                    if ( orderClass.indexOf('sort-desc') > -1 ) {
                        // table is currently sorted in descending order
                        order = 'a';
                    } else if ( orderClass.indexOf('sort-asc') > -1 ) {
                        // table is currently sorted in ascending order
                        order = 'd';
                    }
                }
            }
            table = cell.parents( 'table' );
            if ( table ) {
                parentEl = $( table ).parent();
                if ( parentEl ) {
                    grandparentEl = parentEl.parent();
                    if ( grandparentEl ) {
                        tableRegionId = grandparentEl.attr( "id" );
                        if ( tableRegionId ) {
                            tableRegionName = getRegionNameByDOMID( tableRegionId );
                            field = cell.attr( "field" );
                            if (field && tableRegionName) {
                                sortTable( tableRegionName, field, order );
                            }
                        }
                    }
                }

            }
        }
    }
}

/**
 * Get table header region Backbone view
 * @param additionalField {string} - name of the field in the JSON view model of the header region
 * @param value {string} - table region name
 * @returns {*}
 */
function getTableHeaderRegion( additionalField, value ) {
    var rm,
        model,
        result = null;
    if ( additionalField && value ) {
        rm = getRegionManager();
        if ( rm ) {
            rm.each( function ( region ) {
                if ( region.currentView ) {
                    model = region.currentView.model;
                    if ( model && model.get( additionalField ) === value ) {
                        result = region.currentView;
                    }
                }
            });
        }
    }
    return result;
}

/**
 * Trigger a click event on the header arrow icon
 * @param direction {string} - "previous" or "next"
 * @param header {object} - jQuery header DOM element
 */
function clickHeaderArrow( direction, header ) {
    var arrowClassName,
        arrow;
    if ( direction && header ) {
        arrowClassName = "." + direction + "-page";
        arrow = header.find( arrowClassName );
        if ( arrow && arrow.length === 1 ) {
            arrow.click();
        }
    }
}

/**
 * Create an array of objects that contains table header data
 * @param regionName {string} - region name
 * @param recordCollection {object} - record data Backbone collection
 * @param fieldNames {[string]} - all table field names
 * @param sortOn {[string]} - array of field names that can be sorted
 * @param valueArray {[object]} - array of objects with the values of the table header fields
 * @returns {*}
 */
function getHeaderFields( regionName, recordCollection, fieldNames, sortOn, valueArray ) {

    var fieldName,
        collection,
        compoundName,
        fieldModel,
        valueObject,
        k;

    if ( regionName ) {

        for ( k = 0; k < fieldNames.length; k++ ) {
            fieldName = fieldNames[ k ];
            collection = recordCollection;

            // Nested field names
            if ( fieldName.indexOf(".") > -1 ) {
                compoundName = fieldName.split( "." );
                if ( compoundName && compoundName.length === 2 ) {
                    fieldName = compoundName[ 1 ];
                    // Get nested collection of data models
                    collection = getNestedDataModel( recordCollection, compoundName[ 0 ] );
                }
            }

            fieldModel = lookupFieldModel( collection, fieldName );
            if ( fieldModel ) {
                valueObject = { name: fieldModel.get("label"), field: fieldNames[ k ], type: fieldModel.get( "type" ) };
                valueObject = addSortingFlag( valueObject, regionName, sortOn, fieldName );
            } else {
                valueObject = { name: "", field: fieldName, type: "" };
            }
            valueArray.push( valueObject );
        }
    }

    return valueArray;
}

/**
 * Add sorting boolean flag to the final value object
 * @param valueObject {object} - object with the values of the table header fields
 * @param regionName {string} - region name
 * @param sortOn {[string]} - array of field names that can be sorted
 * @param fieldName {string} - field name
 * @returns {object}
 */
function addSortingFlag( valueObject, regionName, sortOn, fieldName ) {

    var sortOnField,
        l,
        compoundSortName,
        compoundSortVars,
        sortVarsField,
        sortVars;

    sortVars = getRegionSortVars( regionName );
    if ( sortOn ) {
        for ( l = 0; l < sortOn.length; l++ ) {
            sortOnField = sortOn[ l ];
            if ( sortOnField && sortOnField.indexOf(".") > -1 ) {
                compoundSortName = sortOnField.split(".");
                if ( compoundSortName && compoundSortName.length === 2 ) {
                    sortOnField = compoundSortName[ 1 ];
                }
            }
            if ( sortOnField === fieldName ) {
                valueObject.sortable = true;

                // Add sorting order flag ('d' for descending order or 'a' for ascending order) if the table is sorted
                if ( hasKeys( sortVars ) ) {

                    sortVarsField = sortVars.sortField;

                    if ( sortVars.sortField && sortVars.sortField.indexOf(".") > -1 ) {
                        compoundSortVars = sortVars.sortField.split(".");
                        if ( compoundSortVars && compoundSortVars.length === 2 ) {
                            sortVarsField = compoundSortVars[ 1 ];
                        }
                    }

                    if ( sortVarsField && sortVarsField === fieldName ) {
                        valueObject.sorted = true;
                        if ( sortVars.sortOrder ) {
                            valueObject.sortOrder = sortVars.sortOrder;
                        }
                    }
                }
            }
        }
    }
    return valueObject;
}

/**
 * Calls zips3 api to download selected files in folder structure
 * @param table_id {string} - the identifier for the specific table this is being called for
 */
function downloadTableZipFile( table_id ) {

    const table = document.getElementById( table_id + "-table" );
    const checkboxes = table.getElementsByClassName( table_id + '_file_checkbox' );

    let tmp_paths = {};
    for ( let c in checkboxes ) {
        if ( c == 'length' ) { break };

        let checkbox = checkboxes[ c ];
        if ( checkbox.checked ) {                                    
            console.log(checkbox);
            let path = checkbox.getAttribute( "file_path" );
            let s3_file_guid = checkbox.getAttribute( "s3_file_guid" );

            if ( Object.keys( tmp_paths ).includes( path ) ) {
                tmp_paths[ path ].push( s3_file_guid );
            } else {
                tmp_paths[ path ] = [ s3_file_guid ];
            };
        };
    };

    let body_object = {"s3FilesGUID": [] };

    try {
        body_object.username = _headerData.headersData.auid;
    } catch {
        body_object.username = null;
        console.log('unable to get users auid');
    };
    
    for ( let elem of Object.keys( tmp_paths ) ) {
        let tmp_object = { 
            "folder": elem,
            "__s3_file_guid": tmp_paths[ elem ]
         };
         body_object.s3FilesGUID.push( tmp_object )
    };

    console.log("zips3 body object: ", body_object );

    fetch(
        "/data/fsapi/api/v1/zips3/",
        {
            headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
            method: "POST",
            body: JSON.stringify( body_object ),
        }
    )
    .then( res => res.blob() )
    .then( blob => {
        // var file = window.URL.createObjectURL(blob);
        // window.location.assign(file);

        // console.log('blob: ', JSON.stringify(blob));


        const url = window.URL.createObjectURL(blob); // create a new object url
        const a = document.createElement("a"); // create a new anchor element
        a.href = url; // set its href to the object URL

        var today = new Date();
        var dd = String(today.getDate()).padStart(2, '0');
        var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
        var yyyy = today.getFullYear();
        var hh = today.getHours();
        var min = today.getMinutes();
        today = mm + dd + yyyy + "-" + hh + "-" + min;

        a.download = today + ".zip";  // set its download attribute to the deisred filename.
        a.click(); // programmatically clicking the anchor element to trigger the file download.

    });

    // const blob = await response.blob(); // create a new Blob object.
    // const url = window.URL.createObjectURL(blob); // create a new object url
    // const a = document.createElement("a"); // create a new anchor element
    // a.href = url; // set its href to the object URL
    // a.download = "your_file.zip";  // set its download attribute to the deisred filename.
    // a.click(); // programmatically clicking the anchor element to trigger the file download.
}

// print detection listener
window.addEventListener("beforeprint", (event) => {
    console.log("Before print");
    // printable_tables();
});